import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Profile } from "../models/Profile";
import { ProfileService } from "../services/ProfileService";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useMatter } from "./MatterContext";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { getSubdomainFromUrl } from "../utils/url";

interface ProfileContextProps {
  currentProfile: Profile | undefined;
  profiles: Profile[];
  createProfile: (
    fileData: Omit<
      Profile,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ) => Promise<string>;
}

const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined,
);

export const useProfile = (): ProfileContextProps => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !currentMatterId) return;
    const tenantId = getSubdomainFromUrl(window.location.href);
    const docRef = query(
      collection(
        db,
        "tenants",
        tenantId,
        "matters",
        currentMatterId,
        "profiles",
      ),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setProfiles(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as Profile,
          ),
        );
      },
      (err) => {
        setError(`Failed to listen to profile changes: ${err.message}`);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user, currentMatterId]);

  const createProfile = async (
    fileData: Omit<
      Profile,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdBy"
      | "matter"
      | "createdBy"
      | "updatedBy"
    >,
  ): Promise<string> => {
    if (!currentMatterId) return "";
    const newProprofileId = await ProfileService.createProfile(
      currentMatterId!,
      {
        ...fileData,
        createdBy: user!.uid,
        updatedBy: user!.uid,
      },
    );
    return newProprofileId;
  };

  const currentProfile = profiles.find((profile) => profile._id === user?.uid);

  return (
    <ProfileContext.Provider
      value={{
        currentProfile,
        profiles,
        createProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
